import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Season {
  id: string;
  leagueId: string;
  name: string;
  startDate: string;
  endDate: string;
  blackoutPeriods: Array<{ id: string; startDate: string; endDate: string; reason?: string }>;
  fieldEligibility: Array<{ date: string; fieldIds: string[] }>;
  gameSchedule: Array<{ dayOfWeek: number; startTime: string; endTime: string }>;
  teamIds: string[];
  fieldCollectionId?: string;
  isDraft?: boolean;
  createdAt?: string;
  updatedAt?: string;
  league?: { id: string; name: string };
  teams?: Array<{ id: string; name: string; teamNumber: string }>;
  fieldCollection?: { id: string; name: string } | null;
}

const Seasons: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [filteredSeasons, setFilteredSeasons] = useState<Season[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDraft, setFilterDraft] = useState<'all' | 'draft' | 'finalized'>('all');
  const [leagues, setLeagues] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');

  useEffect(() => {
    if (user && !isAdminOrSuperAdmin(user.profileType)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchSeasons();
    fetchLeagues();
  }, []);

  useEffect(() => {
    filterSeasons();
  }, [searchTerm, filterDraft, selectedLeagueId, seasons]);

  const fetchLeagues = async () => {
    try {
      const response = await api.get('/leagues', { params: { page: '1', limit: '100' } });
      const leaguesData = response.data.leagues || response.data;
      setLeagues(leaguesData.map((l: any) => ({ id: l.id, name: l.name })));
    } catch (error) {
      console.error('Error fetching leagues:', error);
    }
  };

  const fetchSeasons = async () => {
    try {
      setLoading(true);
      const response = await api.get('/seasons', { params: { page: '1', limit: '100' } });
      const allSeasons = response.data.seasons || response.data || [];
      
      // Enrich seasons with league and team information
      const enrichedSeasons = await Promise.all(
        allSeasons.map(async (season: Season) => {
          try {
            // Fetch league info
            const leagueRes = await api.get(`/leagues/${season.leagueId}`);
            season.league = { id: leagueRes.data.id, name: leagueRes.data.name };
            
            // Fetch team info
            if (season.teamIds && season.teamIds.length > 0) {
              const teamsData = await Promise.all(
                season.teamIds.map(async (teamId: string) => {
                  try {
                    const teamRes = await api.get(`/teams/${teamId}`);
                    return {
                      id: teamRes.data.id,
                      name: teamRes.data.name,
                      teamNumber: teamRes.data.teamNumber
                    };
                  } catch {
                    return null;
                  }
                })
              );
              season.teams = teamsData.filter(t => t !== null) as Array<{ id: string; name: string; teamNumber: string }>;
            }
            
            // Fetch field collection info if exists
            if (season.fieldCollectionId) {
              try {
                const collectionRes = await api.get(`/fields/collections/${season.fieldCollectionId}`);
                season.fieldCollection = { id: collectionRes.data.id, name: collectionRes.data.name };
              } catch {
                season.fieldCollection = null;
              }
            }
          } catch (error) {
            console.error(`Error enriching season ${season.id}:`, error);
          }
          return season;
        })
      );
      
      setSeasons(enrichedSeasons);
      setFilteredSeasons(enrichedSeasons);
    } catch (error) {
      console.error('Error fetching seasons:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterSeasons = () => {
    let filtered = [...seasons];

    // Filter by draft status
    if (filterDraft === 'draft') {
      filtered = filtered.filter(s => s.isDraft === true);
    } else if (filterDraft === 'finalized') {
      filtered = filtered.filter(s => !s.isDraft);
    }

    // Filter by league
    if (selectedLeagueId) {
      filtered = filtered.filter(s => s.leagueId === selectedLeagueId);
    }

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(term) ||
        s.league?.name.toLowerCase().includes(term) ||
        s.id.toLowerCase().includes(term)
      );
    }

    // Sort by start date (most recent first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      return dateB - dateA;
    });

    setFilteredSeasons(filtered);
  };

  const handleDelete = async (seasonId: string, seasonName: string) => {
    if (!window.confirm(`Are you sure you want to delete the season "${seasonName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/seasons/${seasonId}`);
      alert('Season deleted successfully');
      fetchSeasons();
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to delete season');
    }
  };

  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!isAdminOrSuperAdmin(user?.profileType)) {
    return (
      <div className="container">
        <div className="card">
          <h2>Access Denied</h2>
          <p>Only administrators can manage seasons.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Seasons Management</h1>
        <Link to="/seasons/create" className="btn btn-primary">
          Create New Season
        </Link>
      </div>

      {/* Filters */}
      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
          <div className="form-group">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search by name, league, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', padding: '8px' }}
            />
          </div>
          <div className="form-group">
            <label>Status</label>
            <select
              value={filterDraft}
              onChange={(e) => setFilterDraft(e.target.value as 'all' | 'draft' | 'finalized')}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="all">All Seasons</option>
              <option value="finalized">Finalized</option>
              <option value="draft">Drafts</option>
            </select>
          </div>
          <div className="form-group">
            <label>League</label>
            <select
              value={selectedLeagueId}
              onChange={(e) => setSelectedLeagueId(e.target.value)}
              style={{ width: '100%', padding: '8px' }}
            >
              <option value="">All Leagues</option>
              {leagues.map(league => (
                <option key={league.id} value={league.id}>
                  {league.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Seasons List */}
      <div className="card">
        <h2>All Seasons ({filteredSeasons.length})</h2>
        {filteredSeasons.length === 0 ? (
          <p style={{ color: '#757575', textAlign: 'center', padding: '40px' }}>
            {searchTerm || filterDraft !== 'all' || selectedLeagueId
              ? 'No seasons match your filters.'
              : 'No seasons found. Create your first season to get started.'}
          </p>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {filteredSeasons.map((season) => (
              <div
                key={season.id}
                style={{
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  padding: '20px',
                  background: season.isDraft ? '#fff9e6' : '#fff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h3 style={{ margin: 0 }}>{season.name}</h3>
                      {season.isDraft && (
                        <span className="badge badge-warning" style={{ fontSize: '12px' }}>
                          Draft
                        </span>
                      )}
                      {!season.isDraft && (
                        <span className="badge badge-success" style={{ fontSize: '12px' }}>
                          Finalized
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '5px 0', color: '#757575' }}>
                      <strong>League:</strong> {season.league?.name || 'Unknown'}
                    </p>
                    <p style={{ margin: '5px 0', color: '#757575' }}>
                      <strong>Dates:</strong> {new Date(season.startDate).toLocaleDateString()} - {new Date(season.endDate).toLocaleDateString()}
                    </p>
                    <p style={{ margin: '5px 0', color: '#757575' }}>
                      <strong>Teams:</strong> {season.teams?.length || season.teamIds?.length || 0}
                    </p>
                    {season.fieldCollection && (
                      <p style={{ margin: '5px 0', color: '#757575' }}>
                        <strong>Field Collection:</strong> {season.fieldCollection.name}
                      </p>
                    )}
                    {season.gameSchedule && season.gameSchedule.length > 0 && (
                      <p style={{ margin: '5px 0', color: '#757575' }}>
                        <strong>Game Schedule:</strong>{' '}
                        {season.gameSchedule.map(schedule => 
                          `${daysOfWeek[schedule.dayOfWeek]} ${schedule.startTime}-${schedule.endTime}`
                        ).join(', ')}
                      </p>
                    )}
                    {season.blackoutPeriods && season.blackoutPeriods.length > 0 && (
                      <p style={{ margin: '5px 0', color: '#757575' }}>
                        <strong>Blackout Periods:</strong> {season.blackoutPeriods.length}
                      </p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Link
                      to={`/matches?leagueId=${season.leagueId}&status=all`}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 15px' }}
                    >
                      Matches
                    </Link>
                    {season.isDraft && (
                      <Link
                        to={`/seasons/create?draftId=${season.id}`}
                        className="btn btn-primary"
                        style={{ fontSize: '14px', padding: '8px 15px' }}
                      >
                        Continue Editing
                      </Link>
                    )}
                    <button
                      onClick={() => handleDelete(season.id, season.name)}
                      className="btn btn-danger"
                      style={{ fontSize: '14px', padding: '8px 15px' }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {season.teams && season.teams.length > 0 && (
                  <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                    <strong style={{ fontSize: '14px' }}>Teams:</strong>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                      {season.teams.map(team => (
                        <span key={team.id} className="badge badge-info" style={{ fontSize: '12px' }}>
                          {team.name} ({team.teamNumber})
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Seasons;


